import { User } from '@services/user.interface';
import { AvatarImage, DomainRole, GenesysCloudUser } from '@services/genesys-cloud-user.interface';
import { getAWSRegionByCountryCode, getRegionByCountryCode } from '@utils/region-country-codes.utils';
import { getHighestRankedRole } from '@utils/role-type.utils';
import { RoleType } from '@enums/role-type.enums';

/**
 * Adapt a Genesys Cloud user to a base generic User and defined role
 * @param gcUser - Genesys Cloud User
 * @returns a generic User with a Domain role
 */
export function adaptUser(gcUser: GenesysCloudUser): User {
  return {
    id: gcUser.id,
    name: gcUser.name, // full name
    firstName: getUserFirstName(gcUser.name),
    lastName: getUserLastName(gcUser.name),
    company: gcUser.organization?.name ? gcUser.organization?.name : '',
    userInitials: getUserInitials(gcUser.name),
    email: gcUser.email,
    avatarUri: getBestAvatarImage(gcUser?.images),
    roles:
      gcUser?.authorization?.roles.length > 0
        ? gcUser?.authorization?.roles
        : [{ id: '-1', name: RoleType.UNKNOWN } as DomainRole],
    highestRole: getHighestRankedRole(gcUser?.authorization?.roles),
    region: getRegionByCountryCode(gcUser?.organization?.defaultCountryCode),
    awsRegion: getAWSRegionByCountryCode(gcUser?.organization?.defaultCountryCode),
  } as User;
}

/**
 * Get the best available avatar image from the array of AvatarImage
 * @param images - collection of AvatarImage
 * @returns string - the last valid image URI or empty string if none exists
 */
export function getBestAvatarImage(images: AvatarImage[]): string {
  if (!images?.length) {
    return '';
  }

  // Filter out invalid images (null or undefined imageUri)
  const validImages = images.filter((img) => img?.imageUri);

  if (!validImages.length) {
    return '';
  }

  // Return the last valid image
  return validImages[validImages.length - 1].imageUri;
}

/**
 * Check if Genesys Cloud User has profile images
 * @param images - collection of Genesys Cloud profile user images
 * @returns boolean indicating if Genesys Cloud user has profile images
 */
export function hasAvatarImage(images: AvatarImage[]): boolean {
  let hasImages = false;
  if (images && images.length > 0) {
    if (images[0] && images[0].imageUri) {
      hasImages = true;
    } else {
      hasImages = false;
    }
  }
  return hasImages;
}

/**
 * Get the initials of a user's name
 * @param name - user's name
 * @returns user's initials
 */
export function getUserInitials(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  // First check if the entire name contains any numbers
  if (/\d/.test(name)) {
    return '';
  }

  // Check for names joined by dots without spaces (e.g., 'John.Doe', 'J.D')
  if (/^[A-Za-z.]+$/.test(name)) {
    return '';
  }

  // Split the name into parts, treating spaces, hyphens as separators
  // but keep apostrophes as part of the name
  const nameParts = name
    .trim()
    .replace(/\s*-\s*/g, ' ') // Replace hyphens (and surrounding spaces) with a single space
    .split(' ')
    .filter((part) => {
      // Accept parts that:
      // 1. Have length > 0
      // 2. Either contain only letters OR contain letters with apostrophes/periods
      // 3. Must contain at least one letter
      return (
        part.length > 0 &&
        /[A-Za-z]/.test(part) && // Must contain at least one letter
        (/^[A-Za-z]+$/.test(part) || // Only letters
          /^[A-Za-z.']+$/.test(part)) // Letters with apostrophes or periods
      );
    });

  if (nameParts.length === 0) {
    return '';
  }

  // Always get the first letter of first part and last part
  const firstInitial = nameParts[0][0].toUpperCase();
  const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1][0].toUpperCase() : '';

  return `${firstInitial}${lastInitial}`;
}

/**
 * Get the first name from a full name string
 * @param name - full name string
 * @returns first name
 */
export function getUserFirstName(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }
  return name.trim().split(' ')[0] || '';
}

/**
 * Get the last name from a full name string
 * @param name - full name string
 * @returns last name
 */
export function getUserLastName(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }
  const parts = name.trim().split(' ');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}
